import { SERVICES } from "@/data/services";
import { BLOG_POSTS } from "@/data/blog";
import { MAPS_HREF_SENTINEL } from "@/config/maps";
import type { ServiceItem, SiteContent } from "./types";

const SERVICE_EXTRAS: Record<
  string,
  Pick<ServiceItem, "why" | "process" | "suitableFor" | "note">
> = {
  "motor-ve-mekanik-bakim-onarim": {
    why: "Motor ve mekanik sistemler düzenli kontrol edilmediğinde yüksek maliyetli arızalara neden olabilir.",
    process: "Ön teşhis yapılır, onaylanan işlem planı uygulanır, test sürüşü ile kontrol edilir.",
    suitableFor: ["Performansı düşen araçlar", "Uzun yol öncesi kontrol isteyenler"],
    note: "Onaysız işlem yapılmaz.",
  },
  "dsg-sanziman-bakim-onarim": {
    why: "Erken DSG bakımı, mekatronik arıza riskini azaltır.",
    process: "Diagnostik tarama, adaptasyon kontrolü ve planlı onarım adımları uygulanır.",
    suitableFor: ["Vites geçişinde sarsıntı yaşayan araçlar", "DSG bakım zamanı gelen araçlar"],
    note: "Teslim öncesi adaptasyon doğrulaması yapılır.",
  },
};

const DEFAULT_SERVICES: ServiceItem[] = SERVICES.map((service) => {
  const extra = SERVICE_EXTRAS[service.slug];
  return {
    ...service,
    why: extra?.why ?? "Araç güvenliği ve performans için düzenli kontrol önerilir.",
    process: extra?.process ?? "Detaylı teşhis sonrası işlem planı uygulanır.",
    suitableFor: extra?.suitableFor ?? ["Periyodik bakım isteyen araç sahipleri"],
    note: extra?.note ?? "İşlem adımları önceden paylaşılır.",
  };
});

export const SITE_CONTENT_ROW_ID = "main";

export const DEFAULT_SITE_CONTENT: SiteContent = {
  navigation: {
    logoSrc: "/images/card.png",
    logoAlt: "Car Garage VAG (cargaragevag) Logo",
    links: [
      { label: "ANA SAYFA", href: "/" },
      { label: "HİZMETLER", href: "/hizmetler" },
      { label: "HAKKIMIZDA", href: "/hakkimizda" },
      { label: "YORUMLAR", href: "/yorumlar" },
      { label: "BLOG", href: "/blog" },
      { label: "İLETİŞİM", href: "/iletisim" },
    ],
    instagramUrl: "https://www.instagram.com/cargaragevag/",
    whatsappUrl: "https://wa.me/905373899090",
    mapsDirectionsUrl: MAPS_HREF_SENTINEL,
    ctaLabel: "RANDEVU AL",
    routeCtaLabel: "YOL TARİFİ AL",
  },
  floatingButtons: {
    mapsUrl: MAPS_HREF_SENTINEL,
    instagramUrl: "https://www.instagram.com/cargaragevag/",
    whatsappUrl: "https://wa.me/905373899090",
  },
  footer: {
    description:
      "+20 yıl deneyimle Volkswagen grubu araçlara profesyonel servis hizmetleri sunuyoruz.",
    links: [
      { label: "Ana Sayfa", href: "/" },
      { label: "Hizmetler", href: "/hizmetler" },
      { label: "Hakkımızda", href: "/hakkimizda" },
      { label: "Yorumlar", href: "/yorumlar" },
      { label: "Blog", href: "/blog" },
      { label: "İletişim", href: "/iletisim" },
    ],
    serviceLinks: DEFAULT_SERVICES.map((item) => ({
      label: item.title,
      href: `/hizmetler/${item.slug}`,
    })),
    socialLinks: [
      { icon: "ri-instagram-line", href: "https://www.instagram.com/cargaragevag/" },
      { icon: "ri-facebook-fill", href: "#" },
      { icon: "ri-youtube-line", href: "#" },
      { icon: "ri-whatsapp-line", href: "https://wa.me/905373899090" },
    ],
    contactItems: [
      { icon: "ri-map-pin-2-line", text: "Yeşilbağlar Mah. Azim Sok. No:11\nPendik / İSTANBUL" },
      { icon: "ri-phone-line", text: "0 (537) 389 90 90\n0 (216) 441 99 11" },
      { icon: "ri-mail-line", text: "servis@cargarage.tr" },
      { icon: "ri-time-line", text: "Pzt-Cmt: 08:00-18:30" },
    ],
    copyrightText: "Car Garage VAG (cargaragevag). Tüm hakları saklıdır.",
    partnerText: "Epiked Profesyonel web sistemleri ile hazırlanmıştır.",
    partnerUrl: "https://epiked.com",
    partnerLogo: "/images/elogo.avif",
  },
  home: {
    hero: {
      badge: "Car Garage VAG · Volkswagen Grubu Özel Servis",
      distributorBadge: "Kinergo MSU04 DSG7 DQ200 Türkiye Distribütörü",
      titleLines: ["PROFESYONEL", "ARAÇ SERVİS", "HİZMETLERİ"],
      description:
        "Uzman kadromuzla Volkswagen, Audi, SEAT, Škoda, Porsche ve Cupra araçlara profesyonel hizmet sunuyoruz.",
      subDescription: "Kinergo ürünlerinde Türkiye distribütörüyüz.",
      servicesButtonLabel: "HİZMETLERİMİZ",
      appointmentButtonLabel: "RANDEVU AL",
      scrollLabel: "Aşağı Kaydır",
      videoSrc: "/images/v2.mp4",
      stats: [
        { value: "20+", label: "Yıl Deneyim" },
        { value: "15+", label: "Uzman Teknisyen" },
        { value: "10.000+", label: "Hizmet Alan Araç" },
        { value: "10.000+", label: "Mutlu Müşteri" },
      ],
    },
    kinergo: {
      logoSrc: "/images/kinergo-logo.svg",
      logoAlt: "Kinergo logo",
      badge: "TÜRKİYE DİSTRİBÜTÖRÜ",
      title: "Kinergo MSU04 DSG7 DQ200",
      titleAccent: "SİPARİŞ VE BİLGİ ALANI",
      description: "DSG7 DQ200 için geliştirilen güçlendirilmiş basınç tüpü çözümü.",
      bullets: [
        "Avrupa menşeli ve patentli teknoloji",
        "DSG7 DQ200 için güçlendirilmiş çözüm",
        "Yüksek dayanım ve uzun ömür",
      ],
      cardTitle: "SİPARİŞ İÇİN İLETİŞİME GEÇİN",
      cardDescription: "Sipariş, fiyat ve teknik bilgi için bize ulaşın.",
      cardPrimaryButtonLabel: "SİPARİŞ / BİLGİ TALEBİ",
      cardPhoneLabel: "0 (537) 389 90 90",
      cardEmailLabel: "servis@cargarage.tr",
      productUrl: "https://kinergo.eu/spare/automatic-gearbox-mechatronic-storage-unit/",
      productUrlLabel: "Ürün detay sayfası",
    },
    brands: {
      badge: "Volkswagen Grubu",
      title: "SERVİS VERDİĞİMİZ MARKALAR",
      description: "Volkswagen grubunun tüm markalarına yetkili servis standartlarında hizmet veriyoruz.",
      logos: [
        { name: "SEAT", logo: "/images/seat.png", filter: "brightness(0) invert(1)" },
        { name: "Škoda", logo: "/images/skoda-white.svg", filter: "none" },
        { name: "Volkswagen", logo: "/images/volkswagen.png", filter: "brightness(0) invert(1)" },
        { name: "Audi", logo: "/images/audi.png", filter: "brightness(0) invert(1)" },
        { name: "Cupra", logo: "/images/cupra.png", filter: "brightness(0) invert(1)" },
        { name: "Porsche", logo: "/images/porsche-white.svg", filter: "none" },
      ],
      highlights: [
        "Orijinal Yedek Parça",
        "Sertifikalı Teknisyen",
        "Yetkili Servis Kalitesi",
        "Fabrika Garantisi",
      ],
    },
    services: {
      badge: "Neler Yapıyoruz?",
      title: "HİZMETLERİMİZ",
      description: "Volkswagen grubu araçlarına özel teknik hizmet kategorileri sunuyoruz.",
    },
    about: {
      imageSrc: "/images/baba.jpeg",
      imageAlt: "Car Garage",
      badge: "Biz Kimiz?",
      titleLines: ["YETKİLİ SERVİS", "STANDARTLARINDA", "UZMAN HİZMET"],
      description:
        "Yetkili servis geçmişinden gelen deneyimimizle Volkswagen grubu araçlara profesyonel hizmet sunuyoruz.",
      subDescription: "2025'ten bu yana E-5 üzerindeki tesisimizde hizmet veriyoruz.",
      features: [
        "Orijinal Yedek Parça",
        "VCDS Teşhis Sistemi",
        "Fabrika Eğitimli Teknisyenler",
        "Şeffaf Fiyatlandırma",
      ],
      floatingBadgeValue: "20+ Yıl",
      floatingBadgeLabel: "Sektör Deneyimi",
      stats: [
        { value: 20, suffix: "+", label: "Yıl Deneyim", icon: "ri-award-line" },
        { value: 15, suffix: "+", label: "Uzman Teknisyen", icon: "ri-user-star-line" },
        { value: 10000, suffix: "+", label: "Hizmet Alan Araç", icon: "ri-car-line" },
        { value: 10000, suffix: "+", label: "Mutlu Müşteri", icon: "ri-emotion-happy-line" },
      ],
    },
    testimonials: {
      badge: "Müşteri Yorumları",
      titleLines: ["BİZİM İÇİN NE", "SÖYLÜYORLAR?"],
      items: [
        {
          name: "Murat Kaya",
          car: "VW Golf 8 GTI",
          rating: 5,
          date: "Şubat 2025",
          text: "DSG sorunum kısa sürede tespit edilip çözüldü.",
          avatar: "MK",
          color: "bg-red-600",
        },
        {
          name: "Ayşe Demir",
          car: "Audi A4 2.0 TDI",
          rating: 5,
          date: "Ocak 2025",
          text: "Süreç ve fiyatlandırma çok şeffaftı.",
          avatar: "AD",
          color: "bg-zinc-600",
        },
        {
          name: "Kemal Arslan",
          car: "Škoda Octavia RS",
          rating: 5,
          date: "Aralık 2024",
          text: "Teşhis ve raporlama süreçleri oldukça profesyoneldi.",
          avatar: "KA",
          color: "bg-red-700",
        },
      ],
      ctaTitle: "YETKİLİ SERVİS STANDARTLARINDA HİZMET İÇİN BİZE ULAŞIN",
      ctaDescription: "Randevu alın, uzman ekibimiz aracınızla ilgilensin.",
      ctaButtonLabel: "RANDEVU AL →",
    },
    instagram: {
      title: "@cargaragevag",
      subtitle: "Sosyal Medya",
      profileUrl: "https://www.instagram.com/cargaragevag/",
      images: ["/images/in/ins (1).jpg", "/images/in/ins (2).jpg", "/images/in/ins (3).jpg"],
    },
    contact: {
      badge: "Bize Ulaşın",
      title: "İLETİŞİM BİLGİLERİ",
      description: "Randevu almak veya bilgi edinmek için bize ulaşabilirsiniz.",
      items: [
        {
          icon: "ri-map-pin-2-line",
          title: "Adresimiz",
          lines: ["Yeşilbağlar Mah. Azim Sok. No:11", "Pendik / İSTANBUL"],
        },
        {
          icon: "ri-phone-line",
          title: "Telefon",
          lines: ["0 (537) 389 90 90", "0 (216) 441 99 11"],
        },
        { icon: "ri-mail-line", title: "E-posta", lines: ["servis@cargarage.tr"] },
      ],
    },
  },
  services: {
    hero: {
      badge: "Neler Yapıyoruz?",
      title: "HİZMETLERİMİZ",
      description:
        "Volkswagen grubu araçlarına özel teknik hizmet kategorileriyle aracınızın her ihtiyacına çözüm üretiyoruz.",
      imageSrc: "/images/hero-hizmetler.jpg",
      tagsLine: "9 Hizmet Kategorisi · Orijinal Yedek Parça · VCDS Teşhis",
    },
    cta: {
      badge: "Hemen Başlayalım",
      title: "ARACINIZ İÇİN",
      titleAccent: "RANDEVU ALIN",
      description: "Uzman ekibimiz aracınızı en kısa sürede inceleyip size en doğru çözümü sunsun.",
      buttonLabel: "RANDEVU AL",
      phoneLabel: "0 (537) 389 90 90",
    },
    detail: {
      processSteps: [
        {
          icon: "ri-search-eye-line",
          title: "Araç Kabul ve Teknik İnceleme",
          text: "Aracınız servisimize ulaştığında kapsamlı teknik kontroller yapılır.",
        },
        {
          icon: "ri-clipboard-line",
          title: "Doğru Teşhis ve Planlama",
          text: "Sorunun kaynağı net biçimde belirlenir ve işlem planı oluşturulur.",
        },
        {
          icon: "ri-tools-line",
          title: "Uzman Müdahale ve Onarım",
          text: "Belirlenen işlemler marka standartlarına uygun ekipmanlarla uygulanır.",
        },
        {
          icon: "ri-shield-check-line",
          title: "Son Kontrol ve Teslim",
          text: "Onarım tamamlandığında testler yapılır ve araç güvenle teslim edilir.",
        },
      ],
      quickContactTitle: "Hızlı İletişim",
      quickContactButtonLabel: "RANDEVU AL",
      quickContactWhatsappLabel: "WHATSAPP",
      relatedTitle: "Diğer Hizmetler",
      processSectionBadge: "ADIMLAR",
      processSectionTitle: "Car Garage'da Süreç Nasıl İşler?",
    },
    items: DEFAULT_SERVICES,
  },
  blog: {
    hero: {
      badge: "Bilgi Merkezi",
      title: "BLOG",
      description:
        "Volkswagen grubu araçlara yönelik bakım, arıza teşhisi ve şanzıman teknolojileri hakkında pratik bilgiler paylaşıyoruz.",
    },
    listReadMoreLabel: "Devamını Oku",
    detail: {
      overviewTitle: "Konuya Genel Bakış",
      highlightsTitle: "Öne Çıkan Noktalar",
      faqTitle: "Sık Sorulan Sorular",
      backToListLabel: "Tüm Blog Yazıları",
      notFoundTitle: "Blog yazısı bulunamadı",
      notFoundActionLabel: "Blog sayfasına dön",
      readSuffix: "okuma",
    },
    posts: BLOG_POSTS,
  },
  aboutPage: {
    hero: {
      badge: "Biz Kimiz?",
      title: "HAKKIMIZDA",
      description:
        "Yetkili servis geçmişinden gelen +20 yıllık deneyimle Volkswagen grubu araçlara uzman bakım ve onarım hizmeti sunuyoruz.",
    },
    story: {
      badge: "Kurucumuzdan",
      titleLines: ["20 YILLIK YETKİLİ SERVİS", "DENEYİMİYLE", "CAR GARAGE"],
      paragraphs: [
        "Doğuş Oto ve Yüce Oto yetkili servis altyapısından gelen birikimle 2020'de ilk servisimiz kuruldu.",
        "2025'ten bu yana modern Car Garage tesisimizde kurumsal yapıyla hizmete devam ediyoruz.",
      ],
      features: [
        "Yetkili Servis Sertifikalı Kadro",
        "Garantili Orijinal / OEM Parça",
        "Lisanslı VCDS ve ODIS Teşhisi",
        "Şeffaf Fiyatlandırma",
      ],
      imageSrc: "/images/baba.jpeg",
      imageAlt: "Şirket Sahibi",
      imageBadge: "+20 yıl yetkili servis deneyimi",
    },
    stats: [
      { value: 20, suffix: "+", label: "Yıl Deneyim", icon: "ri-award-line" },
      { value: 15, suffix: "+", label: "Uzman Teknisyen", icon: "ri-user-star-line" },
      { value: 10000, suffix: "+", label: "Hizmet Alan Araç", icon: "ri-car-line" },
      { value: 10000, suffix: "+", label: "Mutlu Müşteri", icon: "ri-emotion-happy-line" },
    ],
    timeline: {
      badge: "Yolculuğumuz",
      title: "TARİHÇEMİZ",
      items: [
        { year: "2020", title: "Kuruluş", desc: "İlk servisimizin açılışı." },
        { year: "2021", title: "Kurumsal Altyapı", desc: "Süreç ve ekipman güçlendirmesi." },
        { year: "2023", title: "Kapasite Artışı", desc: "Bakım ve onarım kapasitesinin büyütülmesi." },
        { year: "2025", title: "Yeni Tesis", desc: "E-5 üzerindeki modern Car Garage tesisine geçiş." },
      ],
    },
  },
  reviewsPage: {
    hero: {
      badge: "Ne Diyorlar?",
      title: "MÜŞTERİ YORUMLARI",
      titleLines: ["MÜŞTERİ", "YORUMLARI"],
      description: "10.000+ hizmet alan araçtan gelen deneyimleri paylaşıyoruz.",
      imageSrc: "/images/hero-yorumlar.jpg",
    },
    summary: [
      { icon: "ri-star-fill", value: "4.9", label: "Ortalama Puan", sub: "500+ değerlendirme", color: "text-amber-400" },
      { icon: "ri-emotion-happy-line", value: "10.000+", label: "Hizmet Alan Araç", sub: "+20 yıllık birikim", color: "text-red-500" },
      { icon: "ri-verified-badge-line", value: "%98", label: "Memnuniyet Oranı", sub: "Geri dönen müşteri", color: "text-green-500" },
    ],
    section: {
      badge: "Gerçek Müşteriler",
      title: "YORUMLAR",
    },
    cta: {
      badge: "Siz de Katılın",
      title: "ARACINIZ İÇİN",
      titleAccent: "RANDEVU ALIN",
      description: "İlk randevunuzda aracınız ücretsiz genel kontrolden geçirilir.",
      buttonLabel: "RANDEVU AL",
    },
    reviews: [
      { name: "Ahmet Kılıç", car: "VW Golf 7 GTI", date: "Ocak 2025", rating: 5, text: "DSG sorunum kısa sürede çözüldü." },
      { name: "Selin Aydın", car: "Audi A4 B9", date: "Şubat 2025", rating: 5, text: "Süreç adım adım anlatıldı, çok memnun kaldım." },
      { name: "Burak Tekin", car: "SEAT Leon FR", date: "Ocak 2025", rating: 5, text: "Bakım süreci şeffaf ve profesyoneldi." },
    ],
  },
  contactPage: {
    hero: {
      badge: "Bize Ulaşın",
      title: "İLETİŞİM",
      description: "Randevu almak veya bilgi almak için bize ulaşın.",
      imageSrc: "/images/hero-iletisim.jpg",
    },
    infoCards: [
      {
        icon: "ri-map-pin-2-line",
        title: "Adres",
        lines: ["Yeşilbağlar Mah. Azim Sok. No:11", "Pendik / İSTANBUL"],
        actionLabel: "Haritada Aç",
        actionHref: MAPS_HREF_SENTINEL,
      },
      {
        icon: "ri-phone-line",
        title: "Telefon",
        lines: ["0 (537) 389 90 90", "0 (216) 441 99 11"],
        actionLabel: "Hemen Ara",
        actionHref: "tel:+905373899090",
      },
      {
        icon: "ri-mail-line",
        title: "E-posta",
        lines: ["servis@cargarage.tr"],
        actionLabel: "Mail Gönder",
        actionHref: "mailto:servis@cargarage.tr",
      },
    ],
    brandsSection: {
      badge: "Uzman Olduğumuz Markalar",
      title: "VOLKSWAGEN GRUBU",
      titleAccent: "ARAÇLARI",
      description: "Aşağıdaki markaların tüm modellerinde uzman ekiplerimizle hizmet veriyoruz.",
      brands: [
        { name: "Volkswagen", logo: "/images/volkswagen.png", filter: "brightness(0) invert(1)" },
        { name: "Audi", logo: "/images/audi.png", filter: "brightness(0) invert(1)" },
        { name: "SEAT", logo: "/images/seat.png", filter: "brightness(0) invert(1)" },
        { name: "Škoda", logo: "/images/skoda-white.svg", filter: "none" },
        { name: "Porsche", logo: "/images/porsche-white.svg", filter: "none" },
        { name: "Cupra", logo: "/images/cupra.png", filter: "brightness(0) invert(1)" },
      ],
    },
    quickActions: {
      badge: "Hızlı Ulaşım",
      title: "HEMEN",
      titleAccent: "BAĞLANIN",
      actions: [
        { icon: "ri-phone-fill", title: "Hemen Ara", value: "0 (537) 389 90 90", href: "tel:+905373899090", className: "bg-red-600 hover:bg-red-700 text-white" },
        { icon: "ri-phone-line", title: "Sabit Hat", value: "0 (216) 441 99 11", href: "tel:+902164419911", className: "bg-zinc-700 hover:bg-zinc-600 text-white" },
        { icon: "ri-whatsapp-line", title: "WhatsApp", value: "Mesaj Gönder", href: "https://wa.me/905373899090", className: "bg-green-700 hover:bg-green-600 text-white" },
        { icon: "ri-route-line", title: "Adres", value: "Yol Tarifi Al", href: MAPS_HREF_SENTINEL, className: "bg-blue-700 hover:bg-blue-600 text-white" },
      ],
    },
  },
};
